import { useState, useEffect, useRef } from 'react';
import './GridReport.scss';
import axios from 'axios';
import { Accordion, Button } from 'react-bootstrap';
import Filters from '../filters/Filters';
import LanguageElementsHandler from '../../repository/languageElementsHandler/LanguageElementsHandler';
import DataGridWithHeaderAndFooter from './dataGridWithHeaderAndFooter/DataGridWithHeaderAndFooter';
import useMain from '../../common/MainContext';

export default function GridReport(props) {
  const { loginData, language, staticsGet } = useMain();

  const FiltersRef = useRef();
  const DataGridWithHeaderAndFooterRef = useRef();
  const [dataLoadingState, setDataLoadingState] = useState('NOT PREPARED');
  const [gridData, setGridData] = useState([]);
  const [gridApi, setGridApi] = useState();
  const [accordionSelectedItem, setAccordionSelectedItem] = useState();

  useEffect(() => {
    if (accordionSelectedItem === 'DATA' || props.report.fixFilter) {
      setDataLoadingState('LOADING');
      return;
    }

    if (
      !props.report.data &&
      !props.report.reportParams.selectedFilters?.length
    ) {
      setDataLoadingState('LOADING');
      return;
    }
  }, [accordionSelectedItem, language]);

  useEffect(() => {
    setDataLoadingState('LOADING');
  }, [props?.report?.preFilter, props?.report?.data, props.refreshId]);

  useEffect(() => {
    switch (dataLoadingState) {
      case 'LOADING':
        loadData();
        return;

      default:
        return;
    }
  }, [dataLoadingState]);

  useEffect(() => {
    if (gridApi) {
      applyHeaderFilters(gridApi);
    }
  }, [gridApi]);

  const languageElementsHandler = new LanguageElementsHandler(
    props?.report?.languageElements,
    language,
  );

  if (!props.id) {
    throw new Error('GridReport.js: props.id is mandatory!');
  }

  function refreshGrid() {
    setDataLoadingState('LOADING');
  }

  function applyHeaderFilters(api) {
    const savedHeaderFilters = staticsGet(['grids', props.id, 'headerFilters']);
    api.setFilterModel(savedHeaderFilters);
  }

  function getFilter() {
    if (props.report.fixFilter) {
      return (props.report.fixFilter === '#') ? '' : props.report.fixFilter;
    }

    if (!props.report.reportParams.selectedFilters?.length) {
      return '';
    }

    return FiltersRef.current.getFilter(props.report);
  }

  function loadData() {
    if (dataLoadingState !== 'LOADING') {
      setDataLoadingState('LOADING');
      return;
    }

    if (props.report.data) {
      setGridData(props.report.data);
      setDataLoadingState('LOADED');
      return;
    }

    axios.get(`${props.dataEndpoint}`, {
      headers: {
        'x-token': loginData.getToken(),
        'x-collection': props.report.collection,
        'x-limit': props.report.maxRecords || 0,
        'x-filter': getFilter(),
        'x-sort': props.report.reportParams.sort,
        'x-language': language,
      },
    }).then((jsonData) => {
      const data = jsonData.data.data;

      setGridData(data);
      setDataLoadingState('LOADED');

      if (props.report.onData) {
        props.report.onData(data);
      }

      if (props.onSelectionChanged) {
        props.onSelectionChanged([]);
      }

      if (props.eventDataLoaded) {
        props.eventDataLoaded(data);
      }
    }).catch((error) => {
      console.error(error);
      setDataLoadingState('NOT LOADED');
    });
  }

  function onGridReady(params) {
    setGridApi(params.api);
    if (props.onGridReady) {
      props.onGridReady(params);
    }
  }

  if (
    props.report.fixFilter ||
    props.report.data ||
    !props.report.reportParams.selectedFilters?.length
  ) {
    return (
      <div className='grid-report'>
        <DataGridWithHeaderAndFooter
          ref={DataGridWithHeaderAndFooterRef}
          id={props.id}
          dataLoadingState={dataLoadingState}
          gridData={gridData}
          report={props.report}
          cellFunctions={props.cellFunctions}
          rowSelection={props.rowSelection}
          isRowSelectable={props.isRowSelectable}
          rowMultiSelectWithClick={props.rowMultiSelectWithClick}
          onSelectionChanged={props.onSelectionChanged}
          onDelete={props.onDelete}
          checkboxColumn={props.checkboxColumn}
          getSelectedRows={props.getSelectedRows}
          getSelectedRowsCallback={props.getSelectedRowsCallback}
          onRowDoubleClick={props.onRowDoubleClick}
          gridHeight={props.gridHeight}
          onGridReady={onGridReady}
          onAddNew={props.onAddNew}
          onRefresh={refreshGrid}
          buttonsOnTop={props.buttonsOnTop}
          setDataLoadingState={setDataLoadingState}
        />
      </div>
    );
  }

  return (
    <div className='grid-report'>
      <Accordion
        onSelect={(selectedItem) => setAccordionSelectedItem(selectedItem)}
      >
        <Accordion.Item eventKey='FILTER' className='accordion-header'>
          <Accordion.Header>
            <Button variant='secondary'>
              {languageElementsHandler.get('flush-filter')}
            </Button>
          </Accordion.Header>
          <Accordion.Body>
            <Filters
              ref={FiltersRef}
              report={props.report}
              languageElements={props.report.languageElements}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey='DATA' className='accordion-item-data'>
          <Accordion.Header>
            <Button variant='secondary'>
              {languageElementsHandler.get('flush-data')}
            </Button>
          </Accordion.Header>
          <Accordion.Body className='fullpage'>
            <DataGridWithHeaderAndFooter
              ref={DataGridWithHeaderAndFooterRef}
              id={props.id}
              dataLoadingState={dataLoadingState}
              gridData={gridData}
              report={props.report}
              cellFunctions={props.cellFunctions}
              rowSelection={props.rowSelection}
              isRowSelectable={props.isRowSelectable}
              rowMultiSelectWithClick={props.rowMultiSelectWithClick}
              onSelectionChanged={props.onSelectionChanged}
              onDelete={props.onDelete}
              checkboxColumn={props.checkboxColumn}
              getSelectedRows={props.getSelectedRows}
              getSelectedRowsCallback={props.getSelectedRowsCallback}
              onRowDoubleClick={props.onRowDoubleClick}
              gridHeight={props.gridHeight}
              onGridReady={onGridReady}
              onAddNew={props.onAddNew}
              onRefresh={refreshGrid}
              buttonsOnTop={props.buttonsOnTop}
              setDataLoadingState={setDataLoadingState}
            />
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
}
